/**
 * Firebase-backed public retrieval.
 *
 * A deliberately thin client over the `retrieveContacts` callable. Every decision
 * that matters — normalisation, HMAC lookup, PIN verification, decryption, and
 * the uniform failure — happens on the server. This module must not add a branch
 * that could distinguish causes of failure.
 *
 * Note the one distinction it *does* make: a transport failure is reported
 * separately from a credential failure. That leaks nothing — a caller already
 * knows whether their own network request completed — and it matters for the
 * person using this. Telling a frightened child "check the phrase and PIN" when
 * the real problem is a dropped connection would have them retype correct
 * credentials over and over.
 */

import { getFunctions, httpsCallable } from "firebase/functions";
import { REGION } from "../../config/regions.js";

/**
 * @param {import("./app.js").FirebaseContext} context
 * @returns {import("../interfaces.js").RetrievalService}
 */
export function createFirebaseRetrievalService(context) {
  const call = httpsCallable(getFunctions(context.app, REGION), "retrieveContacts");

  return {
    async retrieve(phrase, pin) {
      /** @type {unknown} */
      let data;
      try {
        const response = await call({ phrase, pin });
        data = response.data;
      } catch {
        // The request did not complete. Never treated as a credential failure.
        return { ok: false, unreachable: true };
      }

      const result = /** @type {{ ok?: boolean, contacts?: unknown[] }} */ (data);
      if (!result || result.ok !== true || !Array.isArray(result.contacts)) {
        return { ok: false };
      }

      return {
        ok: true,
        contacts: result.contacts.map((raw, index) => {
          const contact =
            /** @type {{ firstName?: string, telephone?: string, relationship?: string }} */ (raw);
          return {
            // The server sends an ordered array with no ids — order is the
            // identity, and it is the order the owner chose for reading under
            // stress. An index-derived id keeps the list renderer happy without
            // inventing state.
            id: `contact:${index}`,
            name: contact.firstName ?? "",
            telephone: contact.telephone ?? "",
            ...(contact.relationship ? { relationship: contact.relationship } : {}),
          };
        }),
      };
    },
  };
}
